import React, {useCallback, useState, useContext, useRef} from 'react';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';
import {DocumentEngagementIdContext, DocumentEntityIdContext} from '../../../../../Main/Forms/CanvasFormContainer';
import {entities, OperationTypes} from '../../../../../../util/uiconstants';
import Utility from '../../../../../../util/utilityfunctions';
import {labels} from '../../../../../../util/utils';
import {isColumnVisibleForEntity} from './config';
import {updateFormBodyRelatedObject} from '../../../../../../actions/FormData/formdataactions';
import {deleteItappItprocessRelation} from '../../../../../../actions/ITApplication/itapplicationaction';
import ClickableIconLabel from '../../../../ClickableIconLabel/ClickableIconLabel';
import EllipsesControl from '../../../../EllipsesControl/EllipsesControl';
import GrayFade from '../../../../GrayFade/GrayFade';
import {FormBodyTypeIdContext} from '../../FormBodyContainer';
import CompareSnapshotLabel from '../../../../CanvasForm/CompareSnapshotLabel';
import {RelatedCanvasForm} from '../../../Section/RelatedCanvasForm';
import {CreateUpdateEntityContainer} from './CreateUpdateEntities/index';
import {useEngagementId, useUserPreferences} from '../../../../../../util/customHooks';

export default function ITApplicationRelatedObject(props) {
	const {data, isTopLevel, bodyId, isSnapshotView} = props;
	const currentLevel = isTopLevel ? 1 : data.level;

	const engagementId = useEngagementId();
	const documentEngagementId = useContext(DocumentEngagementIdContext);
	const documentEntityId = useContext(DocumentEntityIdContext);
	const formBodyTypeId = useContext(FormBodyTypeIdContext);
	const userPreferences = useUserPreferences();
	const modalRef = useRef();

	const [isLoading, setIsLoading] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	const dispatch = useDispatch();

	let isObjectAssociatedToOtherForm = false;

	const aggregateITEvaluationTypeId =
			data.aggregateITEvaluationTypeId === undefined || data.aggregateITEvaluationTypeId === null
				? 0
				: data.aggregateITEvaluationTypeId,
		aggregateITEvaluationTypeLabel =
			labels.aggregateITEvaluationType[aggregateITEvaluationTypeId].aggregateITEvaluationTypeName;

	const serviceOrganizationId = data.isServiceOrganization === true ? 1 : 0,
		serviceOrganizationLabel = labels.itAppTypes[serviceOrganizationId].label;

	const loadEdit = useCallback(() => {
		if (!isSnapshotView && modalRef.current.toggleModal) {
			setIsHovering(false);
			modalRef.current.toggleModal();
		}
	}, [modalRef, isSnapshotView]);

	if (
		(data && data.secondaryRelatedCanvasForm && data.secondaryRelatedCanvasForm.length > 0) ||
		(data && data.primaryRelatedCanvasForm && data.primaryRelatedCanvasForm.length > 0)
	) {
		isObjectAssociatedToOtherForm = true;
	}

	const onItAppSoRemove = useCallback(() => {
		setIsLoading(true);
		dispatch(
			deleteItappItprocessRelation(
				documentEngagementId,
				'itApplication',
				'itProcesses',
				data.parentEntityId,
				data.entityUId
			)
		)
			.then(() => {
				const removedEntity = {
					...data,
					bodyId: bodyId,
					parentEntityUId: data.parentEntityId,
					parentEntityId: entities.ItProcess
				};
				return dispatch(updateFormBodyRelatedObject(removedEntity, OperationTypes.unrelate));
			})
			.finally(() => setIsLoading(false));
	}, [dispatch, bodyId, data, documentEngagementId]);

	// Columns visibility:
	const showITAppType = isColumnVisibleForEntity(
		data.entityId,
		'type',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);
	const showAggregateITEvaluation = isColumnVisibleForEntity(
		data.entityId,
		'aggregateITEvaluation',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);
	const showRelatedCanvasForms = isColumnVisibleForEntity(
		data.entityId,
		'relatedForms',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);

	const showRemoveItAppSo = isColumnVisibleForEntity(
		data.entityId,
		'removeItAppSo',
		bodyId,
		formBodyTypeId,
		documentEntityId,
		isSnapshotView
	);

	const isFormSectionRelatedObject = !bodyId;
	const disabledADCLinkClassName = Utility.getClasstoDisableADCLink(userPreferences, isSnapshotView);

	return (
		<GrayFade isGrayFade={isLoading}>
			<StyledITApplicationRelatedObject
				level={currentLevel}
				className={`entity-content entity-content-level-${currentLevel} ITApplicationRelatedObject`}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
			>
				<section className="Entity-content-list">
					{!isSnapshotView && (
						<CreateUpdateEntityContainer
							bodyId={bodyId}
							entityId={entities.ItSoApplication}
							entityUId={data?.entityUId}
							entityName={data?.entityName}
							isServiceOrganization={data?.isServiceOrganization}
							engagementId={engagementId}
							documentEngagementId={documentEngagementId}
							ref={modalRef}
						/>
					)}
					<section
						className={`entity-content-main${disabledADCLinkClassName ? ' ' + disabledADCLinkClassName : ''}${
							isFormSectionRelatedObject ? ' fsro' : ''
						}`}
					>
						<EllipsesControl onClick={loadEdit} content={data.entityName} tooltip={data.entityName} noOfLines={2} />
					</section>
					{!isSnapshotView ? (
						<>
							{showITAppType && (
								<section className={`entity-content-data SecondCol${isFormSectionRelatedObject ? ' fsro' : ''}`}>
									{serviceOrganizationId !== undefined && (
										<EllipsesControl content={serviceOrganizationLabel} tooltip={serviceOrganizationLabel} />
									)}
								</section>
							)}
							{showRemoveItAppSo && isHovering && (
								<section
									className={`entity-content-data SecondCol remove-btn${isFormSectionRelatedObject ? ' fsro' : ''}`}
								>
									<StyledClickableIconLabel text={labels.remove} onClick={onItAppSoRemove} />
								</section>
							)}
							{showAggregateITEvaluation && (
								<section className={`entity-content-data ThirdCol${isFormSectionRelatedObject ? ' fsro' : ''}`}>
									{aggregateITEvaluationTypeId !== undefined && (
										<EllipsesControl
											content={aggregateITEvaluationTypeLabel}
											tooltip={aggregateITEvaluationTypeLabel}
										/>
									)}
								</section>
							)}
							{showRelatedCanvasForms && (
								<section className={`entity-content-data FourthCol${isFormSectionRelatedObject ? ' fsro' : ''}`}>
									{isObjectAssociatedToOtherForm && (
										<section className="ITAppRelated">
											<RelatedCanvasForm relatedCanvasFormDetails={data} documentEngagementId={documentEngagementId} />
										</section>
									)}
								</section>
							)}
						</>
					) : (
						<CompareSnapshotLabel snapshotObject={data} />
					)}
				</section>
			</StyledITApplicationRelatedObject>
		</GrayFade>
	);
}

const StyledITApplicationRelatedObject = styled.section`
	display: flex;
	&.entity-content {
		&.entity-content-level-2 {
			margin-left: var(--px-20);
			border-bottom: var(--px-1) solid var(--neutrals-200);
			padding: var(--px-5) 0 !important;
			&.ITApplicationRelatedObject {
				margin-left: var(--px-30);
				min-height: var(--px-30);
				.Entity-content-list {
					display: flex;
					width: 100%;
					.entity-content-main {
						display: inline-flex;
						align-items: center;
						width: calc(100% - var(--px-100)) !important;
						padding-right: 0 !important;
						.ellipses,
						.ellipses-multilines {
							cursor: pointer;
							&:hover {
								text-decoration: underline;
							}
						}
						&.disableLinksExternalUsers {
							pointer-events: none;
						}
						&.fsro {
							width: calc(100% - var(--px-550)) !important;
							margin-right: var(--px-10) !important;
						}
					}
					.entity-content-data {
						width: var(--px-250);
						&.SecondCol {
							width: var(--px-330);
							padding: var(--px-2) var(--px-10) 0 var(--px-10);
							color: var(--neutrals-700);
							&.remove-btn {
								display: inline-flex;
								justify-content: flex-end;
								min-width: var(--px-50) !important;
								width: auto !important;
								max-width: var(--px-100);
								padding: 0;
								margin-left: auto;
								margin-right: var(--px-20);
							}
							&.fsro {
								width: var(--px-180);
								padding-left: 0;
							}
						}
						&.ThirdCol {
							width: var(--px-340);
							color: var(--neutrals-700);
							padding-top: var(--px-2);
							padding-left: var(--px-8);
							&.fsro {
								width: var(--px-180);
								padding-left: 0;
							}
						}
						&.FourthCol {
							color: var(--neutrals-700);
							padding-top: var(--px-2);
							padding-left: var(--px-8);
							&.fsro {
								width: var(--px-180);
								padding-left: 0;
								padding-right: 0;
							}
						}
					}
				}
			}
			&:last-child {
				border: 0;
			}
		}
	}
`;

const StyledClickableIconLabel = styled(ClickableIconLabel)``;
